import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, collectionGroup, getDocs, getDoc, getCountFromServer, doc, orderBy, query, where, limit, updateDoc } from 'firebase/firestore';
import { useParchear } from './DatosContext';
import { resumenEliminacion, ejecutarEliminacion } from './operaciones';
import DialogoConfirmar from './Dialogo';
import { leerDiasRevisados, marcarDiaRevisado } from './diasSinTrabajo';

const ANIO = new Date().getFullYear();
const JORNADA_HORAS = 8;
// Sesión 34 · Avisos de antigüedad en "Pendiente de seguimiento" (decisión de Orlando)
const DIAS_AMBAR_OPORTUNIDAD = 5;   // oportunidad sin presupuesto
const DIAS_AMBAR_PRESUPUESTO = 7;   // presupuesto enviado sin respuesta
const DIAS_ROJO_PRESUPUESTO = 15;

function Dashboard({ perfil, usuario, esAdmin, esComercial, esEmpleado, onNavegar }) {
  const [isMobile, setIsMobile] = React.useState(window.innerWidth <= 768);
  React.useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  const [stats, setStats] = useState({
    opAnio: null, opAnioAnterior: null, opEsteMes: null,
    presAnio: null, presAnioAnterior: null,
    presEsteMes: null,
    totalProyectos: 0, proyectosActivos: 0, proyectosIniciadosAnio: 0,
    tasaAnio: { aceptados: 0, conRespuesta: 0, valor: null }, tasaAnioAnterior: { aceptados: 0, conRespuesta: 0, valor: null },
    volumenEnCurso: 0, volumenFacturado: 0, resultado: 0, margenMedio: 0,
    porcentajeResenias: 0,
  });
  const [opsRecientes, setOpsRecientes] = useState([]);
  // Sesión 34: lo que necesita seguimiento (oportunidades sin presupuesto y presupuestos sin respuesta)
  const [pendientes, setPendientes] = useState([]);
  const [horasPendientes, setHorasPendientes] = useState(null); // aviso del último día laborable
  const [proyectosActivosList, setProyectosActivosList] = useState([]);
  const [presupuestosRecientes, setPresupuestosRecientes] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [objetivos, setObjetivos] = useState({
    proyectos: { clave: 9,    estelar: 11   },
    margen:    { clave: 20,   estelar: 28   },
    velocidad: { clave: 1.60, estelar: 1.40 },
    resenias:  { clave: 75,   estelar: 90   },
    horas:     { clave: 1672, estelar: 1712 },
  });
  const [margenEmpresa, setMargenEmpresa] = useState(null);
  // Sesión 34: horas propias del mes (empleado y comercial+empleado) y solicitudes de eliminación (admin)
  const [misHoras, setMisHoras] = useState(null); // { total, dias }
  const [solicitudes, setSolicitudes] = useState([]);

  useEffect(() => {
    if (!esEmpleado || esAdmin || !usuario?.uid) return;
    const hoy = new Date();
    const mes = `${hoy.getFullYear()}-${String(hoy.getMonth() + 1).padStart(2, '0')}`;
    // Siempre con empleadoId: sin ese filtro las reglas rechazan la consulta. Índice empleadoId + fecha.
    getDocs(query(collection(db, 'registros_horas'), where('empleadoId', '==', usuario.uid), where('fecha', '>=', `${mes}-01`), where('fecha', '<=', `${mes}-31`)))
      .then(snap => {
        let total = 0; const dias = new Set();
        snap.forEach(d => { const r = d.data(); total += parseFloat(r.horas) || 0; dias.add(r.fecha); });
        setMisHoras({ total: Math.round(total * 100) / 100, dias: dias.size });
      })
      .catch(e => console.error('No se pudieron leer tus horas del mes', e));
  }, [esEmpleado, esAdmin, usuario?.uid]);

  // Sesión 34: ¿le faltan horas del último día laborable? Mismo aviso que Control de horas. 1 lectura, apuntes propios.
  useEffect(() => {
    if (!esEmpleado || esAdmin || !usuario?.uid) return;
    const d = new Date(); d.setHours(12, 0, 0, 0);
    do { d.setDate(d.getDate() - 1); } while (d.getDay() === 0 || d.getDay() === 6);
    const fecha = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    if (leerDiasRevisados(usuario.uid).includes(fecha)) return; // dado por bueno ("No trabajé ese día" / "Está bien así")
    getDocs(query(collection(db, 'registros_horas'), where('empleadoId', '==', usuario.uid), where('fecha', '==', fecha)))
      .then(snap => {
        const total = snap.docs.reduce((a, x) => a + (parseFloat(x.data().horas) || 0), 0);
        setHorasPendientes(total < JORNADA_HORAS ? { fecha, total, date: d } : null);
      })
      .catch(e => console.error('No se pudieron comprobar tus horas del último día laborable', e));
  }, [esEmpleado, esAdmin, usuario?.uid]);

  useEffect(() => {
    if (!esAdmin) return;
    const fuentes = [
      { col: 'leads', modulo: 'leads', tipo: 'Lead', nombre: d => `${d.nombre || ''} ${d.apellidos || ''}`.trim() },
      { col: 'presupuestos', modulo: 'presupuestos', tipo: 'Presupuesto', nombre: d => d.num_presupuesto || 'Sin número' },
      { col: 'clientes', modulo: 'clientes', tipo: 'Cliente', nombre: d => (d.tipo === 'empresa' ? d.razon_social : `${d.nombre || ''} ${d.apellidos || ''}`.trim()) || 'Sin nombre' },
      { col: 'proyectos', modulo: 'proyectos', tipo: 'Proyecto', nombre: d => `${d.codigo ?? ''} — ${d.nombre || ''}` },
    ];
    // Unas 4 lecturas si no hay solicitudes (1 por consulta vacía)
    Promise.all(fuentes.map(f => getDocs(query(collection(db, f.col), where('solicitud_eliminacion', '!=', null)))
      .then(snap => snap.docs.map(d => ({ id: d.id, col: f.col, modulo: f.modulo, tipo: f.tipo, nombre: f.nombre(d.data()), datos: { id: d.id, ...d.data() }, ...d.data().solicitud_eliminacion })))
      .catch(e => { console.error('No se pudieron leer las solicitudes de', f.col, e); return []; })))
      .then(listas => setSolicitudes(listas.flat()));
  }, [esAdmin]);

  // Sesión 33: un empleado (sin rol admin ni comercial) no puede leer datos comerciales ni fichas de proyecto.
  // Sus cifras salen de la lista reducida de proyectos (sin importes).
  const soloEmpleado = esEmpleado && !esAdmin && !esComercial;
  useEffect(() => { (soloEmpleado ? cargarStatsEmpleado : cargarStats)(); cargarObjetivos(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const cargarStatsEmpleado = async () => {
    try {
      const inicioAnio = new Date(ANIO, 0, 1);
      const cLista = collection(db, 'proyectos_lista');
      const [activos, iniciados, finSnap] = await Promise.all([
        getCountFromServer(query(cLista, where('estado', 'in', ['en_curso', 'entregado']))).then(r => r.data().count),
        getCountFromServer(query(cLista, where('fecha_inicio', '>=', inicioAnio))).then(r => r.data().count),
        getDocs(query(cLista, where('fecha_fin_obra', '>=', inicioAnio))),
      ]);
      let finalizadosAnio = 0, conResenia = 0;
      finSnap.forEach(d => { const p = d.data(); if (p.estado !== 'finalizado') return; finalizadosAnio++; if (p.resenia === true) conResenia++; });
      setStats(s => ({
        ...s, proyectosActivos: activos, proyectosIniciadosAnio: iniciados,
        porcentajeResenias: finalizadosAnio > 0 ? Math.round((conResenia / finalizadosAnio) * 100) : 0,
      }));
    } catch (e) { console.error(e); }
    setCargando(false);
  };

  const cargarStats = async () => {
    // Sesión 31 · El Dashboard cuenta en el servidor (getCountFromServer) y solo descarga
    // conjuntos pequeños. Antes descargaba colecciones enteras (unas 3.650 lecturas por visita,
    // más de 12.000 con las horas importadas). Ahora unas 50.
    try {
      const ahora = new Date();
      const inicioMes = new Date(ahora.getFullYear(), ahora.getMonth(), 1);
      const inicioAnio = new Date(ANIO, 0, 1);
      const inicioAnioAnt = new Date(ANIO - 1, 0, 1);
      const mismoDiaAnioAnt = new Date(ANIO - 1, ahora.getMonth(), ahora.getDate(), 23, 59, 59);
      const contar = async (q) => (await getCountFromServer(q)).data().count;
      const cPres = collection(db, 'presupuestos');
      const cProy = collection(db, 'proyectos');
      const gOps = collectionGroup(db, 'posibles_proyectos');
      const conOp = (snap) => snap.docs.map(d => ({ id: d.id, ...d.data(), leadId: d.ref.parent.parent.id }));

      // Sesión 34: el Dashboard gira en torno a las oportunidades (antes, a los leads). Cifras del año comparadas
      // con el mismo periodo del año anterior. Índices de grupo: estado y fecha_entrada (asc y desc).
      const [
        opEsteMes, opAnio, opAnioAnterior, opRecSnap, opPendSnap,
        presEnvSnap, presRecSnap, presAnioSnap, presAntSnap, presEsteMes,
        totalProyectos, proyActivosSnap, proyectosIniciadosAnio, proyFinAnioSnap,
      ] = await Promise.all([
        contar(query(gOps, where('fecha_entrada', '>=', inicioMes))).catch(e => { console.warn('Oportunidades del mes:', e.message); return null; }),
        contar(query(gOps, where('fecha_entrada', '>=', inicioAnio))).catch(e => { console.warn('Oportunidades del año:', e.message); return null; }),
        contar(query(gOps, where('fecha_entrada', '>=', inicioAnioAnt), where('fecha_entrada', '<=', mismoDiaAnioAnt))).catch(e => { console.warn('Oportunidades del año anterior:', e.message); return null; }),
        getDocs(query(gOps, orderBy('fecha_entrada', 'desc'), limit(5))).catch(e => { console.warn('Últimas oportunidades (¿falta el índice descendente?):', e.message); return null; }),
        getDocs(query(gOps, where('estado', '==', 'pendiente'))).catch(e => { console.warn('Oportunidades pendientes:', e.message); return null; }),
        getDocs(query(cPres, where('estado', '==', 'enviado'))),
        getDocs(query(cPres, orderBy('fecha_presupuesto', 'desc'), limit(5))),
        getDocs(query(cPres, where('fecha_presupuesto', '>=', inicioAnio))),
        getDocs(query(cPres, where('fecha_presupuesto', '>=', inicioAnioAnt), where('fecha_presupuesto', '<=', mismoDiaAnioAnt))),
        contar(query(cPres, where('fecha_presupuesto', '>=', inicioMes))),
        contar(cProy),
        getDocs(query(cProy, where('estado', 'in', ['en_curso', 'entregado']))),
        contar(query(cProy, where('fecha_inicio', '>=', inicioAnio))),
        getDocs(query(cProy, where('fecha_fin_obra', '>=', inicioAnio))),
      ]);

      const opsRec = opRecSnap ? conOp(opRecSnap) : [];
      const opsPend = opPendSnap ? conOp(opPendSnap) : [];
      const presEnv = presEnvSnap.docs.map(d => ({ id: d.id, ...d.data() }));

      // Nombres de los leads: una lectura por lead distinto (pocos). No se descarga la colección entera.
      const idsLead = [...new Set([...opsRec, ...opsPend, ...presEnv].map(x => x.leadId).filter(Boolean))];
      const leadDocs = await Promise.all(idsLead.map(id => getDoc(doc(db, 'leads', id)).catch(() => null)));
      const leadMap = {};
      leadDocs.forEach((d, i) => { if (d && d.exists()) leadMap[idsLead[i]] = d.data(); });
      const nombreLead = (id) => {
        const l = leadMap[id];
        if (!l) return 'Lead no encontrado';
        return `${l.nombre || ''} ${l.apellidos || ''}`.trim() || l.empresa || 'Sin nombre';
      };

      setOpsRecientes(opsRec.map(o => ({ ...o, leadNombre: nombreLead(o.leadId) })));
      setPresupuestosRecientes(presRecSnap.docs.map(d => ({ id: d.id, ...d.data() })));

      const lugar = (o) => [o.sector, o.ciudad || o.oportunidad_ciudad].filter(Boolean).join(' · ');
      setPendientes([
        ...opsPend.map(o => {
          const dias = diasDesde(o.fecha_entrada);
          return {
            clave: `op-${o.id}`, tipo: 'Oportunidad', titulo: nombreLead(o.leadId),
            sub: lugar(o) || 'Sin datos de sector ni ciudad', detalle: `Sin presupuesto · entrada ${cuando(dias)}`,
            dias, nivel: dias !== null && dias >= DIAS_AMBAR_OPORTUNIDAD ? 'ambar' : null,
            nav: { modulo: 'oportunidades', accion: 'ficha', id: o.id, leadId: o.leadId },
          };
        }),
        ...presEnv.map(p => {
          const dias = diasDesde(p.fecha_presupuesto);
          return {
            clave: `pres-${p.id}`, tipo: 'Presupuesto', titulo: p.num_presupuesto || 'Sin número',
            sub: [nombreLead(p.leadId), formatearEuros(p.importe || 0)].join(' · '), detalle: `Sin respuesta · enviado ${cuando(dias)}`,
            dias, nivel: dias === null ? null : dias >= DIAS_ROJO_PRESUPUESTO ? 'rojo' : dias >= DIAS_AMBAR_PRESUPUESTO ? 'ambar' : null,
            nav: { modulo: 'presupuestos', accion: 'ficha', id: p.id },
          };
        }),
      ].sort((a, b) => (b.dias ?? -1) - (a.dias ?? -1)));

      // Tasa de aceptación = aceptados / (aceptados + rechazados). Los "enviado" no cuentan.
      const tasa = (snap) => {
        let a = 0, r = 0;
        snap.forEach(d => { const e = d.data().estado; if (e === 'aceptado') a++; else if (e === 'rechazado') r++; });
        return { aceptados: a, conRespuesta: a + r, valor: a + r > 0 ? (a / (a + r)) * 100 : null };
      };

      // Proyectos activos (en curso + entregados) y cartera
      const proyActivosArr = proyActivosSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      setProyectosActivosList(proyActivosArr.slice(0, 5));
      const volumenEnCurso = proyActivosArr.reduce((s, p) => s + (p.importe_encargo || 0), 0);

      // Finalizados en el año (fecha_fin_obra): facturación, resultado y reseñas
      // Resultado y margen se revisan en la fase 2 (coste de personal con horas).
      let volumenFacturado = 0, resultadoTotal = 0, finalizadosAnio = 0, conResenia = 0;
      proyFinAnioSnap.forEach(d => {
        const p = d.data();
        if (p.estado !== 'finalizado') return;
        finalizadosAnio++;
        if (p.resenia === true) conResenia++;
        const facturado = p.importe_facturado || p.importe_encargo || 0;
        const coste = (p.coste_personal || 0) + (p.costes_produccion || 0);
        volumenFacturado += facturado;
        resultadoTotal += facturado - coste;
      });
      const margenMedioVal = volumenFacturado > 0 ? (resultadoTotal / volumenFacturado) * 100 : 0;

      setStats({
        opAnio, opAnioAnterior, opEsteMes,
        presAnio: presAnioSnap.size, presAnioAnterior: presAntSnap.size, presEsteMes,
        tasaAnio: tasa(presAnioSnap), tasaAnioAnterior: tasa(presAntSnap),
        totalProyectos, proyectosActivos: proyActivosArr.length, proyectosIniciadosAnio,
        volumenEnCurso, volumenFacturado, resultado: resultadoTotal,
        margenMedio: Math.round(margenMedioVal * 10) / 10,
        porcentajeResenias: finalizadosAnio > 0 ? Math.round((conResenia / finalizadosAnio) * 100) : 0,
      });
    } catch (e) { console.error(e); }
    setCargando(false);
  };

  const cargarObjetivos = async () => {
    try {
      const snapObj = await getDoc(doc(db, 'config', 'objetivos'));
      if (snapObj.exists()) {
        const data = snapObj.data();
        if (data[ANIO]) setObjetivos(data[ANIO]);
      }
      const snapMar = await getDoc(doc(db, 'config', 'margen_empresa'));
      if (snapMar.exists()) {
        const data = snapMar.data();
        const mesActual = new Date().getMonth();
        const margenMes = data[String(ANIO)]?.[mesActual];
        if (margenMes !== undefined) setMargenEmpresa(margenMes);
      }
    } catch (e) { console.error('Error cargando objetivos:', e); }
  };

  // Tasa de aceptación del año en curso, comparada con el mismo periodo del año anterior (sesión 31)
  const tasaConversion = stats.tasaAnio;

  if (cargando) return <div style={{...styles.container, padding: isMobile ? '16px' : '32px'}}><p style={{ color: '#AFA9EC' }}>Cargando dashboard...</p></div>;

  return (
    <div style={{...styles.container, padding: isMobile ? '16px' : '32px'}}>
      <div style={styles.cabecera}>
        <div>
          <h1 style={styles.titulo}>Bienvenido, {perfil?.nombre || usuario?.email}</h1>
          <p style={styles.fecha}>{formatearFecha(new Date())}</p>
        </div>
        {/* Botón registrar horas: solo para empleado (no comercial, no admin) */}
        {esEmpleado && !esAdmin && (
          <button
            style={styles.botonHoras}
            onClick={() => onNavegar({ modulo: 'horas', accion: 'nuevo' })}
          >
            + Registrar horas de hoy
          </button>
        )}
      </div>

      {esAdmin && solicitudes.length > 0 && (
        <BloqueSolicitudes solicitudes={solicitudes} setSolicitudes={setSolicitudes} onNavegar={onNavegar} />
      )}

      {(() => {
        // Sesión 34: "Pendiente de seguimiento" arriba del todo. El empleado ve sus horas pendientes; el comercial,
        // lo que espera respuesta; quien es las dos cosas, ambas.
        const itemHoras = horasPendientes ? [{
          clave: 'horas', tipo: 'Horas',
          titulo: `Horas del ${horasPendientes.date.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'numeric' })}`,
          sub: horasPendientes.total > 0 ? `Solo tienes ${fmtHoras(horasPendientes.total)} apuntadas` : 'No tienes horas apuntadas ese día',
          detalle: 'Último día laborable', dias: null, nivel: 'ambar', boton: 'Apuntar',
          boton2: { texto: horasPendientes.total > 0 ? 'Está bien así' : 'No trabajé ese día', onClick: () => { marcarDiaRevisado(usuario?.uid, horasPendientes.fecha); setHorasPendientes(null); } },
          nav: { modulo: 'horas', accion: 'nuevo', fecha: horasPendientes.fecha },
        }] : [];
        const props = { stats, tasaConversion, opsRecientes, presupuestosRecientes, onNavegar };
        if (esAdmin) return (
          <>
            <BloquePendientes titulo="Pendiente de seguimiento" items={pendientes} onNavegar={onNavegar} vacio="Nada pendiente de seguimiento" />
            <DashboardAdmin {...props} proyectosActivosList={proyectosActivosList} objetivos={objetivos} margenEmpresa={margenEmpresa} />
          </>
        );
        if (esComercial) return (
          <>
            <BloquePendientes titulo="Pendiente de seguimiento" items={[...itemHoras, ...pendientes]} onNavegar={onNavegar} vacio="Nada pendiente de seguimiento" />
            <DashboardComercial {...props} misHoras={esEmpleado ? misHoras : null} />
          </>
        );
        return (
          <>
            <BloquePendientes titulo="Pendiente" items={itemHoras} onNavegar={onNavegar} vacio="Tus horas están al día" />
            <DashboardEmpleado misHoras={misHoras} stats={stats} onNavegar={onNavegar} objetivos={objetivos} margenEmpresa={margenEmpresa} />
          </>
        );
      })()}
    </div>
  );
}

// ─── SOLICITUDES DE ELIMINACIÓN (sesión 34) ───────────────────────────────────
// El comercial pide; el administrador decide aquí mismo: Ver la ficha, Rechazar o Eliminar.
// Eliminar usa el mismo borrado que las fichas (operaciones.js) y siempre pide confirmación con lo que se borra.
const TIPO_OP = { leads: 'lead', presupuestos: 'presupuesto', clientes: 'cliente', proyectos: 'proyecto' };

function BloqueSolicitudes({ solicitudes, setSolicitudes, onNavegar }) {
  const parchearLeads = useParchear('leads');
  const parchearOps = useParchear('oportunidades');
  const parchearPres = useParchear('presupuestos');
  const parchearClientes = useParchear('clientes');
  const parchearProyectos = useParchear('proyectos');
  const parchear = { leads: parchearLeads, presupuestos: parchearPres, clientes: parchearClientes, proyectos: parchearProyectos };
  const [confirmando, setConfirmando] = useState(null); // { s, mensaje }
  const [ocupado, setOcupado] = useState(null); // id en curso
  const [error, setError] = useState('');

  const quitar = (s) => setSolicitudes(prev => prev.filter(x => !(x.col === s.col && x.id === s.id)));

  const rechazar = async (s) => {
    setOcupado(s.id); setError('');
    try {
      await updateDoc(doc(db, s.col, s.id), { solicitud_eliminacion: null });
      parchear[s.col](prev => prev.map(x => x.id === s.id ? { ...x, solicitud_eliminacion: null } : x));
      quitar(s);
    } catch (e) { console.error(e); setError('No se pudo rechazar la solicitud. Inténtalo de nuevo.'); }
    setOcupado(null);
  };

  const pedirEliminar = async (s) => {
    setError('');
    setConfirmando({ s, mensaje: 'Comprobando qué se va a borrar…', listo: false });
    try {
      const mensaje = await resumenEliminacion(TIPO_OP[s.col], s.id, s.datos);
      setConfirmando({ s, mensaje, listo: true });
    } catch (e) {
      console.error(e); setConfirmando(null); setError('No se pudo preparar la eliminación. Abre la ficha con «Ver».');
    }
  };

  const eliminar = async () => {
    const { s } = confirmando;
    setOcupado(s.id);
    try {
      const r = await ejecutarEliminacion(TIPO_OP[s.col], s.id, s.datos);
      parchear[s.col](prev => prev.filter(x => x.id !== s.id));
      if (s.col === 'leads') parchearOps(prev => prev.filter(o => o.leadId !== s.id));
      if (s.col === 'presupuestos') {
        if (r.estadoOp) parchearOps(prev => prev.map(o => o.id === r.oportunidadId ? { ...o, estado: r.estadoOp } : o));
        if (r.proyectoId) parchearProyectos(prev => prev.map(p => p.id === r.proyectoId ? { ...p, presupuestoId: '' } : p));
      }
      if (s.col === 'proyectos') {
        const libres = new Set(r.presupuestosLiberados || []);
        parchearPres(prev => prev.map(p => libres.has(p.id) ? { ...p, proyectoId: null } : p));
      }
      quitar(s);
      setConfirmando(null);
    } catch (e) { console.error(e); setError('No se pudo completar la eliminación. Abre la ficha con «Ver» para revisar cómo ha quedado.'); setConfirmando(null); }
    setOcupado(null);
  };

  const boton = { background: 'transparent', borderRadius: '8px', padding: '6px 12px', fontSize: '0.78rem', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', whiteSpace: 'nowrap' };
  return (
    <div style={{ ...styles.bloque, border: '1px solid rgba(186,117,23,0.45)', marginBottom: '24px' }}>
      {confirmando && (
        <DialogoConfirmar
          titulo={`Eliminar ${confirmando.s.tipo.toLowerCase()}`}
          mensaje={confirmando.mensaje}
          textoSeguro="No eliminar"
          textoPeligroso={ocupado ? 'Eliminando…' : 'Sí, eliminar'}
          onSeguro={() => { if (!ocupado) setConfirmando(null); }}
          onPeligroso={() => { if (confirmando.listo && !ocupado) eliminar(); }}
          ocupado={!!ocupado || !confirmando.listo}
        />
      )}
      <h3 style={{ ...styles.bloqueTitulo, color: '#BA7517' }}>Solicitudes de eliminación · {solicitudes.length}</h3>
      {error && <p style={{ color: '#D85A30', fontSize: '0.82rem', marginBottom: '8px' }}>{error}</p>}
      {solicitudes.map(s => (
        <div key={`${s.col}-${s.id}`} style={{ ...styles.filaBloque, cursor: 'default', flexWrap: 'wrap' }}>
          <div style={{ ...styles.filaBloqueIzq, minWidth: '220px' }}>
            <span style={styles.filaNombre}>{s.tipo} · {s.nombre}</span>
            <span style={styles.filaSub}>
              {s.por_nombre || 'Alguien'}{s.fecha?.toDate ? ` · ${formatearFechaCorta(s.fecha.toDate())}` : ''} · Motivo: {s.motivo || 'sin motivo'}
            </span>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button style={{ ...boton, color: '#AFA9EC', border: '1px solid rgba(83,74,183,0.4)' }} onClick={() => onNavegar({ modulo: s.modulo, accion: 'ficha', id: s.id })}>Ver</button>
            <button style={{ ...boton, color: '#fff', background: '#534AB7', border: '1px solid #534AB7' }} disabled={ocupado === s.id} onClick={() => rechazar(s)}>{ocupado === s.id ? '…' : 'Rechazar'}</button>
            <button style={{ ...boton, color: '#D85A30', border: '1px solid rgba(216,90,48,0.6)' }} disabled={!!ocupado} onClick={() => pedirEliminar(s)}>Eliminar {TIPO_OP[s.col]}…</button>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── DASHBOARD ADMIN ──────────────────────────────────────────────────────────

function DashboardAdmin({ stats, tasaConversion, opsRecientes, proyectosActivosList, presupuestosRecientes, onNavegar, objetivos, margenEmpresa }) {
  return (
    <>
      {/* KPIs fila 1 — pulso comercial (sesión 34: año en curso frente al mismo periodo del año anterior) */}
      <div style={styles.gridKpis}>
        <TarjetaComparada titulo={`Oportunidades ${ANIO}`} valor={stats.opAnio} anterior={stats.opAnioAnterior} mes={stats.opEsteMes} onClick={() => onNavegar({ modulo: 'oportunidades' })} />
        <TarjetaComparada titulo={`Presupuestos ${ANIO}`} valor={stats.presAnio} anterior={stats.presAnioAnterior} mes={stats.presEsteMes} onClick={() => onNavegar({ modulo: 'presupuestos' })} />
        <KpiCard titulo={`Tasa de aceptación ${ANIO}`} valor={fmtPct1(tasaConversion.valor)} sub={subTasa(tasaConversion, stats.tasaAnioAnterior)} color={colorTasa(tasaConversion.valor)} />
        <KpiCard titulo="Proyectos activos" valor={stats.proyectosActivos} sub="en curso o entregados" color="#7B6FE8" onClick={() => onNavegar({ modulo: 'proyectos' })} />
      </div>

      {/* KPIs fila 2 — económico (se revisa en la auditoría de cálculos) */}
      <div style={{ ...styles.gridKpis, gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}>
        <KpiCard titulo="Cartera en curso" valor={formatearEuros(stats.volumenEnCurso)} sub="encargos activos (en curso + entregados)" color="#7B6FE8" onClick={() => onNavegar({ modulo: 'proyectos' })} />
        <KpiCard titulo={`Facturación ${ANIO}`} valor={formatearEuros(stats.volumenFacturado)} sub="proyectos finalizados en el año" color={stats.volumenFacturado > 0 ? '#1D9E75' : '#AFA9EC'} />
        <KpiCard titulo={`Resultado ${ANIO}`} valor={formatearEuros(stats.resultado)} sub="facturado − personal − producción" color={stats.resultado > 0 ? '#1D9E75' : stats.resultado < 0 ? '#D85A30' : '#AFA9EC'} />
        <KpiCard titulo="Margen medio" valor={stats.margenMedio > 0 ? fmtPct1(stats.margenMedio) : '—'} sub="proyectos finalizados en el año" color={stats.margenMedio >= 50 ? '#1D9E75' : stats.margenMedio >= 35 ? '#BA7517' : stats.margenMedio > 0 ? '#D85A30' : '#AFA9EC'} />
      </div>

      {/* Objetivos */}
      <div style={styles.seccion}>
        <h2 style={styles.seccionTitulo}>Objetivos {ANIO}</h2>
        <div style={styles.gridObjetivos}>
          <ObjetivoCard titulo="Proyectos iniciados"     valor={stats.proyectosIniciadosAnio}      clave={objetivos.proyectos?.clave} estelar={objetivos.proyectos?.estelar} unidad=""        inversion={false} />
          <ObjetivoCard titulo="Margen empresa"          valor={margenEmpresa}             clave={objetivos.margen?.clave}    estelar={objetivos.margen?.estelar}    unidad="%"       inversion={false} esManual={true} />
          <ObjetivoCard titulo="Velocidad de entrega"    valor={null} pendiente="Pendiente de importar horas"      clave={objetivos.velocidad?.clave} estelar={objetivos.velocidad?.estelar} unidad=" hr/m²"  inversion={true}  nota="Menor valor = mejor" />
          <ObjetivoCard titulo="Reseñas conseguidas"     valor={stats.porcentajeResenias}  clave={objetivos.resenias?.clave}  estelar={objetivos.resenias?.estelar}  unidad="%"       inversion={false} />
          <ObjetivoCard titulo="Horas registradas (equipo)" valor={null} pendiente="Pendiente de importar horas"        clave={objetivos.horas?.clave}     estelar={objetivos.horas?.estelar}     unidad=" h"      inversion={false} />
        </div>
      </div>

      <AccesosRapidos onNavegar={onNavegar} conAdmin />

      {/* Actividad reciente */}
      <div style={styles.seccion}>
        <h2 style={styles.seccionTitulo}>Actividad reciente</h2>
        <div style={styles.gridBloques}>
          <BloqueUltimasOportunidades ops={opsRecientes} onNavegar={onNavegar} />
          <BloqueUltimosPresupuestos presupuestos={presupuestosRecientes} onNavegar={onNavegar} conImporte />

          {/* Proyectos activos */}
          <div style={styles.bloque}>
            <h3 style={styles.bloqueTitulo}>Proyectos en curso</h3>
            {proyectosActivosList.length === 0
              ? <p style={styles.vacio}>Sin proyectos activos</p>
              : proyectosActivosList.map(proy => (
                <div key={proy.id} style={styles.filaBloque}
                  onClick={() => onNavegar({ modulo: 'proyectos', accion: 'ficha', id: proy.id })}
                  onMouseEnter={e => e.currentTarget.style.background = 'rgba(83,74,183,0.06)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  <div style={styles.filaBloqueIzq}>
                    <span style={styles.filaNombre}>{proy.nombre || proy.codigo || '—'}</span>
                    <span style={styles.filaSub}>{proy.sector || '—'}</span>
                  </div>
                  <div style={styles.filaDer}>
                    <span style={styles.filaImporte}>{formatearEuros(proy.importe_encargo || 0)}</span>
                  </div>
                </div>
              ))}
          </div>
        </div>
      </div>
    </>
  );
}

// ─── DASHBOARD COMERCIAL (con o sin rol de empleado) ─────────────────────────
// Sesión 34: un solo dashboard comercial. Si también es empleado, la cuarta tarjeta son sus horas del mes.
function DashboardComercial({ stats, tasaConversion, opsRecientes, presupuestosRecientes, onNavegar, misHoras }) {
  const conHoras = misHoras !== null && misHoras !== undefined;
  return (
    <>
      <div style={styles.gridKpis}>
        <TarjetaComparada titulo={`Oportunidades ${ANIO}`} valor={stats.opAnio} anterior={stats.opAnioAnterior} mes={stats.opEsteMes} onClick={() => onNavegar({ modulo: 'oportunidades' })} />
        <TarjetaComparada titulo={`Presupuestos ${ANIO}`} valor={stats.presAnio} anterior={stats.presAnioAnterior} mes={stats.presEsteMes} onClick={() => onNavegar({ modulo: 'presupuestos' })} />
        <KpiCard titulo={`Tasa de aceptación ${ANIO}`} valor={fmtPct1(tasaConversion.valor)} sub={subTasa(tasaConversion, stats.tasaAnioAnterior)} color={colorTasa(tasaConversion.valor)} />
        {conHoras
          ? <KpiMisHoras misHoras={misHoras} onNavegar={onNavegar} />
          : <KpiCard titulo="Proyectos activos" valor={stats.proyectosActivos} sub={`de ${fmtEntero(stats.totalProyectos)} totales`} color="#7B6FE8" onClick={() => onNavegar({ modulo: 'proyectos' })} />}
      </div>

      <AccesosRapidos onNavegar={onNavegar} />

      <div style={styles.seccion}>
        <h2 style={styles.seccionTitulo}>Actividad reciente</h2>
        <div style={styles.gridBloques}>
          <BloqueUltimasOportunidades ops={opsRecientes} onNavegar={onNavegar} />
          <BloqueUltimosPresupuestos presupuestos={presupuestosRecientes} onNavegar={onNavegar} />
        </div>
      </div>
    </>
  );
}

// ─── BLOQUES COMPARTIDOS (sesión 34) ──────────────────────────────────────────

// Accesos rápidos en el orden natural del trabajo (lead → oportunidad → presupuesto → proyecto). "Registrar horas" no
// aparece para empleados porque ya tienen el botón de la cabecera; el admin no lo tiene y lo conserva aquí.
function AccesosRapidos({ onNavegar, conAdmin = false }) {
  return (
    <div style={styles.seccion}>
      <h2 style={styles.seccionTitulo}>Accesos rápidos</h2>
      <div style={styles.gridAccesos}>
        <AccesoRapido titulo="Nuevo lead"        descripcion="Registrar contacto comercial"   icono={<svg width="20" height="20" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><circle cx="8" cy="5.5" r="2.5"/><path d="M3 14c0-3 2.2-4.5 5-4.5s5 1.5 5 4.5"/></svg>} onClick={() => onNavegar({ modulo: 'leads', accion: 'nuevo' })} />
        <AccesoRapido titulo="Nueva oportunidad" descripcion="Nuevo encargo posible de un lead" icono={<svg width="20" height="20" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"><path d="M8 1.5l1.9 4 4.3.5-3.2 3 .9 4.3L8 11.2l-3.9 2.1.9-4.3-3.2-3 4.3-.5z"/></svg>} onClick={() => onNavegar({ modulo: 'oportunidades', accion: 'nueva' })} />
        <AccesoRapido titulo="Nuevo presupuesto" descripcion="Crear propuesta económica"      icono={<svg width="20" height="20" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"><line x1="8" y1="1" x2="8" y2="15"/><path d="M11 4.5C11 3 9.5 2.5 8 2.5 6 2.5 5 3.5 5 5c0 1.5 1 2 3 2.5 2 .5 3.5 1 3.5 3 0 1.5-1.5 3-3.5 3-2 0-3.5-1-3.5-2.5"/></svg>} onClick={() => onNavegar({ modulo: 'presupuestos', accion: 'nuevo' })} />
        {conAdmin && <AccesoRapido titulo="Nuevo proyecto"  descripcion="Iniciar proyecto"             icono={<svg width="20" height="20" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M1 7L8 1.5L15 7"/><path d="M2.5 6.5V14H6V10H10V14H13.5V6.5"/><rect x="6.5" y="10" width="3" height="4"/></svg>} onClick={() => onNavegar({ modulo: 'proyectos', accion: 'nuevo' })} />}
        {conAdmin && <AccesoRapido titulo="Registrar horas" descripcion="Control de tiempo del equipo" icono={<svg width="20" height="20" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><circle cx="8" cy="8" r="6.5"/><polyline points="8,4 8,8 11,10"/></svg>} onClick={() => onNavegar({ modulo: 'horas', accion: 'nuevo' })} />}
      </div>
    </div>
  );
}

// Pendiente de seguimiento: oportunidades sin presupuesto y presupuestos sin respuesta, de la más antigua a la
// más reciente. Ámbar y rojo según los días (constantes al principio del archivo).
const COLOR_NIVEL = { ambar: '#BA7517', rojo: '#D85A30' };
function BloquePendientes({ titulo, items, onNavegar, vacio }) {
  return (
    <div style={{ ...styles.bloque, marginBottom: '24px' }}>
      <h3 style={{ ...styles.bloqueTitulo, display: 'flex', alignItems: 'center', gap: '8px' }}>
        {titulo}
        {items.length > 0 && <span style={{ background: '#534AB7', color: '#fff', borderRadius: '20px', padding: '1px 8px', fontSize: '0.7rem', letterSpacing: 0 }}>{items.length}</span>}
      </h3>
      {items.length === 0
        ? <p style={{ ...styles.vacio, textAlign: 'left', padding: '4px 0' }}>{vacio}</p>
        : items.map(it => {
          const color = COLOR_NIVEL[it.nivel];
          return (
            <div key={it.clave}
              style={{ ...styles.filaBloque, flexWrap: 'wrap', borderLeft: `3px solid ${color || 'transparent'}`, paddingLeft: '12px' }}
              onClick={() => onNavegar(it.nav)}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(83,74,183,0.06)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
              <div style={{ ...styles.filaBloqueIzq, minWidth: '180px' }}>
                <span style={{ color: '#AFA9EC', fontSize: '0.66rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{it.tipo}</span>
                <span style={styles.filaNombre}>{it.titulo}</span>
                <span style={styles.filaSub}>{it.sub}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexShrink: 0, marginLeft: 'auto' }}>
                <span style={{ color: color || '#AFA9EC', fontSize: '0.78rem', fontWeight: color ? 500 : 400 }}>{it.detalle}</span>
                {it.boton2 && <button style={styles.botonVer} onClick={e => { e.stopPropagation(); it.boton2.onClick(); }}>{it.boton2.texto}</button>}
                <button style={styles.botonVer} onClick={e => { e.stopPropagation(); onNavegar(it.nav); }}>{it.boton || 'Ver'}</button>
              </div>
            </div>
          );
        })}
    </div>
  );
}

function BloqueUltimasOportunidades({ ops, onNavegar }) {
  return (
    <div style={styles.bloque}>
      <h3 style={styles.bloqueTitulo}>Últimas oportunidades</h3>
      {ops.length === 0
        ? <p style={styles.vacio}>Sin oportunidades registradas</p>
        : ops.map(op => (
          <div key={op.id} style={styles.filaBloque}
            onClick={() => onNavegar({ modulo: 'oportunidades', accion: 'ficha', id: op.id, leadId: op.leadId })}
            onMouseEnter={e => e.currentTarget.style.background = 'rgba(83,74,183,0.06)'}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
            <div style={styles.filaBloqueIzq}>
              <span style={styles.filaNombre}>{op.leadNombre}</span>
              <span style={styles.filaSub}>{[op.sector, op.ciudad].filter(Boolean).join(' · ') || '—'}</span>
            </div>
            <div style={styles.filaDer}>
              <EstadoBadge estado={op.estado} />
              <span style={styles.filaFecha}>{op.fecha_entrada?.toDate ? formatearFechaCorta(op.fecha_entrada.toDate()) : '—'}</span>
            </div>
          </div>
        ))}
    </div>
  );
}

function BloqueUltimosPresupuestos({ presupuestos, onNavegar, conImporte = false }) {
  return (
    <div style={styles.bloque}>
      <h3 style={styles.bloqueTitulo}>Últimos presupuestos</h3>
      {presupuestos.length === 0
        ? <p style={styles.vacio}>Sin presupuestos registrados</p>
        : presupuestos.map(pres => (
          <div key={pres.id} style={styles.filaBloque}
            onClick={() => onNavegar({ modulo: 'presupuestos', accion: 'ficha', id: pres.id })}
            onMouseEnter={e => e.currentTarget.style.background = 'rgba(83,74,183,0.06)'}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
            <div style={styles.filaBloqueIzq}>
              <span style={styles.filaNombre}>{pres.num_presupuesto || '—'}</span>
              <span style={styles.filaSub}>{conImporte ? `${pres.sector || '—'} · ${pres.tipo_encargo || '—'}` : (pres.sector || '—')}</span>
            </div>
            <div style={styles.filaDer}>
              <EstadoBadge estado={pres.estado} />
              {conImporte && <span style={styles.filaImporte}>{formatearEuros(pres.importe || 0)}</span>}
            </div>
          </div>
        ))}
    </div>
  );
}

// Tarjeta con la cifra del año en curso y su comparación con el mismo periodo del año anterior; el mes, en pequeño.
function TarjetaComparada({ titulo, valor, anterior, mes, onClick }) {
  const hay = valor !== null && valor !== undefined && anterior !== null && anterior !== undefined;
  const dif = hay && anterior > 0 ? ((valor - anterior) / anterior) * 100 : null;
  const colorDif = dif === null ? '#AFA9EC' : dif >= 0 ? '#1D9E75' : '#D85A30';
  return (
    <KpiCard titulo={titulo} valor={valor === null || valor === undefined ? '—' : fmtEntero(valor)} color="#7B6FE8" onClick={onClick}
      sub={<>
        {hay
          ? <>{fmtEntero(anterior)} a estas alturas de {ANIO - 1}{dif !== null && <> · <span style={{ color: colorDif, fontWeight: 500 }}>{fmtVariacion(dif)}</span></>}</>
          : 'Sin comparación disponible'}
        <br /><span style={{ opacity: 0.75 }}>Este mes: {mes === null || mes === undefined ? '—' : fmtEntero(mes)}</span>
      </>} />
  );
}

// ─── DASHBOARD EMPLEADO ───────────────────────────────────────────────────────

function DashboardEmpleado({ misHoras, stats, onNavegar, objetivos, margenEmpresa }) {
  return (
    <>
      <div style={styles.gridKpis}>
        <KpiMisHoras misHoras={misHoras} onNavegar={onNavegar} />
        <KpiCard titulo="Proyectos activos" valor={stats.proyectosActivos} sub="en curso" color="#7B6FE8" />
      </div>
      <div style={styles.seccion}>
        <h2 style={styles.seccionTitulo}>Objetivos {ANIO}</h2>
        <div style={styles.gridObjetivos}>
          <ObjetivoCard titulo="Proyectos iniciados"  valor={stats.proyectosIniciadosAnio}     clave={objetivos.proyectos?.clave} estelar={objetivos.proyectos?.estelar} unidad=""       inversion={false} />
          <ObjetivoCard titulo="Margen empresa"       valor={margenEmpresa}            clave={objetivos.margen?.clave}    estelar={objetivos.margen?.estelar}    unidad="%"      inversion={false} esManual={true} />
          <ObjetivoCard titulo="Velocidad de entrega" valor={null} pendiente="Pendiente de importar horas"     clave={objetivos.velocidad?.clave} estelar={objetivos.velocidad?.estelar} unidad=" hr/m²" inversion={true}  nota="Menor valor = mejor" />
          <ObjetivoCard titulo="Reseñas conseguidas"  valor={stats.porcentajeResenias} clave={objetivos.resenias?.clave} estelar={objetivos.resenias?.estelar}  unidad="%"      inversion={false} />
          <ObjetivoCard titulo="Horas registradas"    valor={null} pendiente="Pendiente de importar horas"          clave={objetivos.horas?.clave}     estelar={objetivos.horas?.estelar}     unidad=" h"     inversion={false} />
        </div>
      </div>
    </>
  );
}

// ─── COMPONENTES AUXILIARES ───────────────────────────────────────────────────

// Sesión 34: horas propias del mes en curso (antes "pendiente de importar horas")
function KpiMisHoras({ misHoras, onNavegar }) {
  const fmt = (n) => `${new Intl.NumberFormat('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n)} h`;
  const mes = new Date().toLocaleDateString('es-ES', { month: 'long' });
  return (
    <KpiCard titulo={`Mis horas · ${mes}`}
      valor={misHoras ? fmt(misHoras.total) : '—'}
      sub={misHoras ? `${misHoras.dias} ${misHoras.dias === 1 ? 'día' : 'días'} con horas` : 'cargando...'}
      color={misHoras && misHoras.total > 0 ? '#7B6FE8' : '#AFA9EC'}
      onClick={() => onNavegar({ modulo: 'horas' })} />
  );
}

function KpiCard({ titulo, valor, sub, color, onClick }) {
  return (
    <div
      style={{ ...styles.kpiCard, cursor: onClick ? 'pointer' : 'default' }}
      onClick={onClick}
      onMouseEnter={e => { if (onClick) e.currentTarget.style.borderColor = 'rgba(83,74,183,0.5)'; }}
      onMouseLeave={e => { if (onClick) e.currentTarget.style.borderColor = 'rgba(83,74,183,0.2)'; }}
    >
      <p style={styles.kpiTitulo}>{titulo}</p>
      <p style={{ ...styles.kpiValor, color }}>{valor ?? '—'}</p>
      <p style={styles.kpiSub}>{sub}</p>
    </div>
  );
}

function AccesoRapido({ titulo, descripcion, icono, onClick }) {
  return (
    <div
      style={styles.accesoCard}
      onClick={onClick}
      onMouseEnter={e => e.currentTarget.style.borderColor = '#534AB7'}
      onMouseLeave={e => e.currentTarget.style.borderColor = 'rgba(83,74,183,0.2)'}
    >
      <span style={styles.accesoIcono}>{icono}</span>
      <div>
        <p style={styles.accesoTitulo}>{titulo}</p>
        <p style={styles.accesoDesc}>{descripcion}</p>
      </div>
    </div>
  );
}

function EstadoBadge({ estado }) {
  const colores = { pendiente: '#534AB7', contactar: '#534AB7', perdido: '#BA7517', basura: '#6b7280', cliente: '#1D9E75', enviado: '#534AB7', aceptado: '#1D9E75', rechazado: '#D85A30', descartado: '#6b7280', presupuestado: '#BA7517', en_curso: '#534AB7', entregado: '#BA7517', finalizado: '#1D9E75' };
  const etiquetas = { pendiente: 'Pendiente', contactar: 'Contactar', perdido: 'Perdido', basura: 'Basura', cliente: 'Cliente', enviado: 'Enviado', aceptado: 'Aceptado', rechazado: 'Rechazado', descartado: 'Descartado', presupuestado: 'Presupuestado', en_curso: 'En curso', entregado: 'Entregado', finalizado: 'Finalizado' };
  return (
    <span style={{ background: colores[estado] || '#534AB7', color: '#fff', fontSize: '0.68rem', fontWeight: '500', padding: '2px 8px', borderRadius: '20px', whiteSpace: 'nowrap' }}>
      {etiquetas[estado] || estado}
    </span>
  );
}

function ObjetivoCard({ titulo, valor, clave, estelar, unidad, inversion, esManual, nota, pendiente }) {
  const sinDatos = valor === null || valor === undefined || valor === 0;
  const progreso = sinDatos ? 0 : inversion
    ? Math.max(0, Math.min(100, ((clave - valor) / (clave - estelar)) * 100))
    : Math.max(0, Math.min(100, (valor / estelar) * 100));
  const badge = pendiente ? 'Pendiente' : sinDatos ? 'Sin datos' : inversion
    ? (valor <= estelar ? 'Estelar' : valor <= clave ? 'En camino' : 'Por mejorar')
    : (valor >= estelar ? 'Estelar' : valor >= clave ? 'En camino' : 'Por mejorar');
  const badgeColor = badge === 'Estelar' ? '#1D9E75' : badge === 'En camino' ? '#534AB7' : (badge === 'Sin datos' || badge === 'Pendiente') ? '#6b7280' : '#D85A30';

  // Format numeric values with Spanish locale (comma as decimal separator)
  const fmtValor = (v) => {
    if (v === null || v === undefined) return '—';
    if (typeof v !== 'number') return v;
    // Use 2 decimals if value has decimals, 0 if integer
    const decimals = Number.isInteger(v) ? 0 : 2;
    return new Intl.NumberFormat('es-ES', { minimumFractionDigits: decimals, maximumFractionDigits: decimals }).format(v);
  };

  return (
    <div style={styles.objetivoCard}>
      <div style={styles.objetivoHeader}>
        <p style={styles.objetivoTitulo}>{titulo}</p>
        <span style={{ ...styles.badge, background: badgeColor }}>{badge}</span>
      </div>
      <p style={styles.objetivoValor}>
        {pendiente ? '—' : <>{fmtValor(valor)}{unidad}</>}
        {esManual && <span style={{ fontSize: '0.7rem', color: '#BA7517', marginLeft: '8px' }}>introducido manualmente</span>}
      </p>
      {(pendiente || nota) && <p style={{ fontSize: '0.72rem', color: '#BA7517', marginBottom: '8px' }}>{pendiente || nota}</p>}
      <div style={styles.barraFondo}><div style={{ ...styles.barraRelleno, width: `${progreso}%` }} /></div>
      <div style={styles.objetivoMetas}>
        <span style={styles.metaTexto}>Objetivo Clave: {fmtValor(clave)}{unidad}</span>
        <span style={styles.metaTexto}>Objetivo Estelar: {fmtValor(estelar)}{unidad}</span>
      </div>
    </div>
  );
}

function formatearFecha(fecha) {
  const dias = ['Domingo','Lunes','Martes','Miércoles','Jueves','Viernes','Sábado'];
  const meses = ['enero','febrero','marzo','abril','mayo','junio','julio','agosto','septiembre','octubre','noviembre','diciembre'];
  return `${dias[fecha.getDay()]}, ${fecha.getDate()} de ${meses[fecha.getMonth()]} de ${fecha.getFullYear()}`;
}

function formatearFechaCorta(fecha) {
  const meses = ['ene','feb','mar','abr','may','jun','jul','ago','sep','oct','nov','dic'];
  return `${fecha.getDate()} ${meses[fecha.getMonth()]}`;
}

// Porcentaje con 1 decimal, formato es-ES (sesión 31)
function fmtPct1(v) {
  if (v === null || v === undefined) return '—';
  return `${new Intl.NumberFormat('es-ES', { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(v)}%`;
}
function colorTasa(v) {
  if (v === null || v === undefined) return '#AFA9EC';
  return v >= 50 ? '#1D9E75' : v >= 30 ? '#BA7517' : '#D85A30';
}
function subTasa(t, tAnt) {
  return `${t.aceptados} de ${t.conRespuesta} con respuesta · a estas alturas de ${ANIO - 1}: ${fmtPct1(tAnt?.valor)}`;
}

// Sesión 34: enteros con separador de miles también en 4 cifras; variación con signo y 1 decimal
function fmtEntero(n) {
  return new Intl.NumberFormat('es-ES', { maximumFractionDigits: 0, useGrouping: 'always' }).format(n || 0);
}
function fmtVariacion(v) {
  return `${new Intl.NumberFormat('es-ES', { minimumFractionDigits: 1, maximumFractionDigits: 1, signDisplay: 'exceptZero' }).format(v)}%`;
}
function fmtHoras(n) {
  return `${new Intl.NumberFormat('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0)} h`;
}
// Días naturales desde una fecha (Timestamp o Date) hasta hoy; null si no hay fecha
function diasDesde(v) {
  const f = v?.toDate ? v.toDate() : v instanceof Date ? v : null;
  if (!f) return null;
  const a = new Date(f); a.setHours(0, 0, 0, 0);
  const hoy = new Date(); hoy.setHours(0, 0, 0, 0);
  return Math.max(0, Math.round((hoy - a) / 86400000));
}
function cuando(dias) {
  if (dias === null) return 'sin fecha';
  if (dias === 0) return 'hoy';
  if (dias === 1) return 'ayer';
  return `hace ${dias} días`;
}

function formatearEuros(valor) {
  return new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(valor);
}

const styles = {
  container: { padding: '32px', maxWidth: '100%' }, // mobile override via isMobile
  cabecera: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '32px', flexWrap: 'wrap', gap: '16px' },
  titulo: { color: '#ffffff', fontSize: '1.6rem', fontWeight: '300', letterSpacing: '0.02em', marginBottom: '4px' },
  fecha: { color: '#AFA9EC', fontSize: '0.85rem' },
  botonHoras: { background: '#534AB7', color: '#ffffff', border: 'none', borderRadius: '8px', padding: '12px 20px', fontSize: '0.9rem', fontWeight: '500', cursor: 'pointer', whiteSpace: 'nowrap', fontFamily: 'DM Sans, sans-serif' },
  gridKpis: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '24px' },
  kpiCard: { background: '#1a1440', borderRadius: '12px', padding: '20px 24px', border: '1px solid rgba(83,74,183,0.2)', transition: 'border-color 0.15s' },
  kpiTitulo: { color: '#AFA9EC', fontSize: '0.78rem', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '8px' },
  kpiValor: { fontSize: '2rem', fontWeight: '300', marginBottom: '4px' },
  kpiSub: { color: '#AFA9EC', fontSize: '0.78rem' },
  seccion: { marginBottom: '32px' },
  seccionTitulo: { color: '#ffffff', fontSize: '1rem', fontWeight: '400', letterSpacing: '0.05em', marginBottom: '16px', paddingBottom: '8px', borderBottom: '1px solid rgba(83,74,183,0.2)' },
  gridAccesos: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '12px' },
  accesoCard: { background: '#1a1440', border: '1px solid rgba(83,74,183,0.2)', borderRadius: '10px', padding: '16px 20px', display: 'flex', alignItems: 'center', gap: '14px', cursor: 'pointer', transition: 'border-color 0.15s', minHeight: '72px' },
  accesoIcono: { fontSize: '1.4rem', color: '#7B6FE8', flexShrink: 0 },
  accesoTitulo: { color: '#ffffff', fontSize: '0.88rem', fontWeight: '500', marginBottom: '2px' },
  accesoDesc: { color: '#AFA9EC', fontSize: '0.75rem', lineHeight: '1.3' },
  gridBloques: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '16px' },
  bloque: { background: '#1a1440', borderRadius: '12px', padding: '20px 24px', border: '1px solid rgba(83,74,183,0.2)' },
  bloqueTitulo: { color: '#AFA9EC', fontSize: '0.78rem', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '16px' },
  filaBloque: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 8px', borderBottom: '1px solid rgba(83,74,183,0.1)', gap: '12px', cursor: 'pointer', borderRadius: '6px', transition: 'background 0.15s' },
  filaBloqueIzq: { display: 'flex', flexDirection: 'column', gap: '2px', flex: 1, overflow: 'hidden' },
  filaDer: { display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px', flexShrink: 0 },
  filaNombre: { color: '#ffffff', fontSize: '0.88rem', fontWeight: '500', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  filaSub: { color: '#AFA9EC', fontSize: '0.75rem' },
  filaFecha: { color: '#AFA9EC', fontSize: '0.75rem' },
  filaImporte: { color: '#7B6FE8', fontSize: '0.85rem', fontWeight: '500' },
  botonVer: { background: 'transparent', color: '#AFA9EC', border: '1px solid rgba(83,74,183,0.45)', borderRadius: '6px', padding: '5px 12px', fontSize: '0.78rem', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', whiteSpace: 'nowrap' },
  vacio: { color: '#AFA9EC', fontSize: '0.85rem', textAlign: 'center', padding: '20px 0' },
  gridObjetivos: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '16px' },
  objetivoCard: { background: '#1a1440', borderRadius: '12px', padding: '20px 24px', border: '1px solid rgba(83,74,183,0.2)' },
  objetivoHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px', gap: '8px' },
  objetivoTitulo: { color: '#AFA9EC', fontSize: '0.82rem', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.05em' },
  badge: { color: '#ffffff', fontSize: '0.7rem', fontWeight: '500', padding: '3px 8px', borderRadius: '20px', whiteSpace: 'nowrap', alignSelf: 'flex-start', marginTop: '2px', flexShrink: 0 },
  objetivoValor: { color: '#ffffff', fontSize: '1.8rem', fontWeight: '300', marginBottom: '12px' },
  barraFondo: { background: 'rgba(83,74,183,0.2)', borderRadius: '4px', height: '6px', marginBottom: '8px', overflow: 'hidden' },
  barraRelleno: { background: '#534AB7', height: '100%', borderRadius: '4px', transition: 'width 0.4s ease' },
  objetivoMetas: { display: 'flex', justifyContent: 'space-between' },
  metaTexto: { color: '#AFA9EC', fontSize: '0.72rem' },
};

export default Dashboard;
